import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { AuthLayout } from "./AuthLayout";

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return "Too many attempts. Try again later.";
  }
  return "Could not reach the server.";
}

/**
 * The public self-service counterpart to PasswordResetPanel (admin-
 * triggered). Always shows the SAME generic confirmation regardless of
 * what actually happened server-side — unknown username, no email on
 * file, or a genuine send are all indistinguishable here on purpose
 * (httpx/forgotpassword.go's own account-enumeration-safety contract).
 */
export function ForgotPassword() {
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.forgotPassword(username);
      setDone(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <div className="card signin">
          <h1>Check your email</h1>
          <p className="shell__subtitle">
            If an account with that username exists and has an email on file, a reset link has
            been sent to it.
          </p>
          <a href="/">Back to sign in</a>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className="card signin" onSubmit={(event) => void handleSubmit(event)}>
        <h1>Forgot your password?</h1>
        <p className="shell__subtitle">Enter your username and we&rsquo;ll email you a reset link.</p>
        <label className="signin__field">
          <span>Username</span>
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            autoFocus
          />
        </label>
        {error ? (
          <p className="card__error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" disabled={submitting}>
          {submitting ? "Sending…" : "Send reset link"}
        </button>
        <a className="auth__link" href="/">
          Back to sign in
        </a>
      </form>
    </AuthLayout>
  );
}
